import { useEffect, useCallback, useRef, useState } from 'react';
import { IMessage } from '@stomp/stompjs';
import { websocketService } from '../../utils/websocket/websocket.service';

/**
 * Hook to use WebSocket connection and subscribe to topics
 */
export const useWebSocket = (
  wsUrl?: string,
  autoConnect: boolean = true,
) => {
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!autoConnect) {
      console.log('[useWebSocket] autoConnect is false, skipping connection');
      return;
    }

    const connect = async () => {
      console.log('[useWebSocket] Initiating WebSocket connection');
      try {
        await websocketService.connect(wsUrl);
        setConnected(true);
        console.log('[useWebSocket] ✅ Connected to WebSocket');
      } catch (error) {
        console.error('[useWebSocket] ❌ Failed to connect to WebSocket:', error);
        setConnected(false);
      }
    };

    // Check if already connected
    if (websocketService.isConnected()) {
      console.log('[useWebSocket] Already connected');
      setConnected(true);
    } else {
      connect();
    }

    return () => {
      // Don't disconnect on unmount, keep connection alive for other components
      // websocketService.disconnect();
    };
  }, [wsUrl, autoConnect]);

  const subscribe = useCallback(
    (topic: string, handler: (message: IMessage) => void) => {
      console.log('[useWebSocket] Subscribe called for topic:', topic);

      if (!websocketService.isConnected()) {
        console.warn('[useWebSocket] ⚠️ WebSocket not connected yet, cannot subscribe to:', topic);
        return () => {};
      }

      return websocketService.subscribe(topic, handler);
    },
    [],
  );

  return { subscribe, isConnected: connected };
};

/**
 * Hook to subscribe to incident updates
 */
export const useIncidentWebSocket = (
  incidentId?: number,
  onIncidentUpdate?: (incident: any) => void,
) => {
  const { subscribe, isConnected } = useWebSocket();
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    console.log('[useIncidentWebSocket] Effect running, isConnected:', isConnected, 'incidentId:', incidentId);
    if (!isConnected || !incidentId) return;

    const topic = `/ws/incident/${incidentId}`;
    console.log('[useIncidentWebSocket] Subscribing to:', topic);

    unsubscribeRef.current = subscribe(topic, (message: IMessage) => {
      console.log('[useIncidentWebSocket] 📨 Message received:', message.body);
      if (onIncidentUpdate) {
        try {
          const incident = JSON.parse(message.body);
          console.log('[useIncidentWebSocket] ✅ Parsed incident:', incident);
          onIncidentUpdate(incident);
        } catch (error) {
          console.error('[useIncidentWebSocket] ❌ Error parsing incident message:', error);
        }
      }
    });

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [incidentId, isConnected, subscribe, onIncidentUpdate]);
};

/**
 * Hook to subscribe to incident item (activity) updates for a specific incident.
 * Backend sends new activities to /ws/incident-item/{incidentId}
 */
export const useIncidentItemWebSocket = (
  incidentId?: number | string,
  onActivityReceived?: (activity: any) => void,
) => {
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const onActivityReceivedRef = useRef(onActivityReceived);

  useEffect(() => {
    onActivityReceivedRef.current = onActivityReceived;
  });

  useEffect(() => {
    if (!incidentId) return;
    let cancelled = false;

    console.log('[useIncidentItemWebSocket] Connecting for incidentId:', incidentId);

    websocketService.connect().then(() => {
      if (cancelled) {
        console.log('[useIncidentItemWebSocket] ⚠️ Cancelled before subscribe, skipping. incidentId:', incidentId);
        return;
      }
      const topic = `/ws/incident-item/${incidentId}`;
      console.log('[useIncidentItemWebSocket] ✅ Connected, subscribing to:', topic);
      unsubscribeRef.current = websocketService.subscribe(
        topic,
        (message: IMessage) => {
          console.log('[useIncidentItemWebSocket] 📨 Message received on', topic, ':', message.body);
          try {
            const activity = JSON.parse(message.body);
            console.log('[useIncidentItemWebSocket] ✅ Parsed activity:', activity);
            onActivityReceivedRef.current?.(activity);
          } catch (err) {
            console.error('[useIncidentItemWebSocket] ❌ Parse error:', err);
          }
        },
      );
    }).catch((err) => {
      console.error('[useIncidentItemWebSocket] ❌ Connection failed:', err);
    });

    return () => {
      console.log('[useIncidentItemWebSocket] 🧹 Cleanup, unsubscribing. incidentId:', incidentId);
      cancelled = true;
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
    };
  }, [incidentId]);
};

/**
 * Hook to subscribe to all incidents list updates
 * Backend sends both created and updated incidents to /ws/incidents
 */
export const useIncidentsListWebSocket = (
  onIncidentReceived?: (incident: any) => void,
  topic: string = '/ws/incident',
) => {
  const { subscribe, isConnected } = useWebSocket();
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    console.log('[useIncidentsListWebSocket] Effect running, isConnected:', isConnected);
    if (!isConnected) {
      console.log('[useIncidentsListWebSocket] Not connected yet, skipping subscription');
      return;
    }

    if (!onIncidentReceived) {
      console.log('[useIncidentsListWebSocket] No handler provided, skipping subscription');
      return;
    }

    console.log('[useIncidentsListWebSocket] 🔌 Connected! Subscribing to', topic);

    // Backend sends notifications to /ws/incident with { institutionId, action }
    unsubscribeRef.current = subscribe(topic, (message: IMessage) => {
      console.log('[useIncidentsListWebSocket] 📨 RAW message on', topic, ':', message.body);
      try {
        const incident = JSON.parse(message.body);
        console.log('[useIncidentsListWebSocket] ✅ Parsed incident:', incident);
        onIncidentReceived(incident);
      } catch (error) {
        console.error('[useIncidentsListWebSocket] ❌ Error parsing incident message:', error);
      }
    });

    return () => {
      console.log('[useIncidentsListWebSocket] Cleaning up subscription');
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [isConnected, subscribe, onIncidentReceived, topic]);
};
